import type { ReactNode } from "react";
import { TotpLoginStep } from "./TotpLoginStep";
import { TotpEnroll, type TotpEnrollProps } from "./TotpEnroll";

// 로그인 전 화면의 두 카드 — 로그인 2단계 · 강제 등록. 조각(TotpLoginStep · TotpEnroll)만 공통으로 두고
// 카드는 서비스가 감싸게 했더니, 같은 화면이 서비스마다 폭(380 · 500 · 710)·제목·로그아웃 자리가 갈렸다
// (2026-10-10 실측, t-mops-totp-unify). 그래서 b3x-in 이 쓰던 카드를 그대로 여기 올렸다. 카드는 v3 의
// `.b-auth-card`(폭 380) 이고, 화면 바탕(`.b-auth` — 가운데 정렬)은 부르는 쪽 것이다 — 서비스마다 사진·
// 언어 선택 같은 껍데기가 다르다.
//
// 서비스마다 다를 수 있는 자리는 둘뿐이다.
// - head: 서비스 로그인 카드 머리(로고 줄 · eyebrow). 비밀번호 카드와 같은 머리를 2단계 카드에도 두려는 자리다.
//   제목·안내를 여기 더 적지 않는다 — 조각이 이미 말한다(두 번 읽힌다).
// - className: 카드 바탕을 바꾸는 서비스 클래스(사진 위 유리판 등). 폭·간격을 바꾸는 클래스를 넣지 않는다.

export interface TotpLoginCardProps {
  onSubmit: (code: string) => Promise<void>;
  onRestart: () => void;
  head?: ReactNode;
  className?: string;
  // 사전(i18n)이 있는 서비스가 같은 값을 넘기는 자리. 안 주면 조각의 기본 문장.
  title?: string;
  note?: string;
}

/** 로그인 2단계 카드 — 비밀번호가 맞아 대기 토큰을 받은 뒤. 제목은 안쪽 조각이 단다. */
export function TotpLoginCard({ onSubmit, onRestart, head, className, title = "2단계 인증", note }: TotpLoginCardProps) {
  return (
    <section className={cardClass(className)} aria-label={title}>
      {head}
      <TotpLoginStep onSubmit={onSubmit} onRestart={onRestart} title={title} note={note} />
    </section>
  );
}

export interface TotpEnrollGateProps extends Omit<TotpEnrollProps, "enabled" | "required" | "onDisable"> {
  // 정책이 등록을 요구하는 화면이라 해제 단추는 안 그려진다(TotpEnroll required) — 그래도 조각이 요구하는
  // 자리라 받되, 안 주면 거절로 채운다.
  onDisable?: TotpEnrollProps["onDisable"];
  // 로그아웃. 이 화면은 등록을 마치기 전에는 다른 데로 못 가므로 나가는 길이 하나는 있어야 한다.
  onLogout: () => void;
  head?: ReactNode;
  className?: string;
  title?: string;
  logoutLabel?: string;
}

/** 강제 등록 카드 — 정책이 2단계를 요구하는데 미등록일 때 로그인 직후 이것만 열린다.
 *  막는 것은 화면이 아니라 서버 관문(403)이다. 이 카드는 그 사실을 보여 주고 등록 길을 연다. */
export function TotpEnrollGate({
  onLogout, head, className, title = "2단계 인증 등록이 필요합니다", logoutLabel = "로그아웃",
  onDisable = () => Promise.reject(new Error("이 계정은 2단계 인증이 필수라 해제할 수 없습니다.")),
  ...enroll
}: TotpEnrollGateProps) {
  return (
    <section className={cardClass(className)} aria-label={title}>
      {head}
      {/* 「이 계정은 2단계 인증이 필수」 안내는 안쪽 조각이 한다 — 여기서 또 적으면 두 번 읽힌다. */}
      <h1 className="b-auth-title">{title}</h1>
      <TotpEnroll enabled={false} required onDisable={onDisable} {...enroll} />
      <button type="button" className="mtotp-link" onClick={onLogout}>
        <span aria-hidden="true">← </span>
        {logoutLabel}
      </button>
    </section>
  );
}

function cardClass(extra?: string): string {
  return extra ? `b-auth-card ${extra}` : "b-auth-card";
}
